<script setup>
import TicketTable from '@frontJs/components/tables/projectManagement/tasks/TicketTable.vue';
import TenantReportTicketTable from '@frontJs/components/tables/projectManagement/tasks/tenantReports/TicketTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { h, onMounted, ref } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const loading = ref(true);

const data = ref({
    toBeProcessed: 0,
    ongoing: 0,
    toBeApproved: 0,
    pendingCustomerResponse: 0,
    completed: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(route('front.projectManagement.task.postTicketCount'), {
            toBeProcessed: true,
            tenantId: tenant ? tenant.id : null,
        }),
        axios.post(route('front.projectManagement.task.postTicketCount'), {
            inProgress: true,
            tenantId: tenant ? tenant.id : null,
        }),
        axios.post(route('front.projectManagement.task.postTicketCount'), {
            toBeApproved: true,
            tenantId: tenant ? tenant.id : null,
        }),
        axios.post(route('front.projectManagement.task.postTicketCount'), {
            pendingCustomerResponse: true,
            tenantId: tenant ? tenant.id : null,
        }),
        axios.post(route('front.projectManagement.task.postTicketCount'), {
            completed: true,
            tenantId: tenant ? tenant.id : null,
        }),
    ])
        .then((response) => {
            data.value.toBeProcessed = response[0].data.count;
            data.value.inProgress = response[1].data.count;
            data.value.toBeApproved = response[2].data.count;
            data.value.pendingCustomerResponse = response[3].data.count;
            data.value.completed = response[4].data.count;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    updateData();
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>Tickets Apikom - Tâches - Gestion de projet - Datakepler</title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - Tâches - Tickets Apikom
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            toBeProcessed: {
                title: 'À traiter',
                total: data.toBeProcessed,
                content: h(TicketTable, {
                    tenant,
                    toBeProcessed: true,
                    callback: updateData,
                }),
            },
            inProgress: {
                title: 'En cours',
                total: data.inProgress,
                content: h(TicketTable, {
                    tenant,
                    inProgress: true,
                    callback: updateData,
                }),
            },
            toBeApproved: {
                title: 'À valider',
                total: data.toBeApproved,
                content: h(TicketTable, {
                    tenant,
                    toBeApproved: true,
                    callback: updateData,
                }),
            },
            pendingCustomerResponse: {
                title: 'En attente client',
                total: data.pendingCustomerResponse,
                content: h(TicketTable, {
                    tenant,
                    pendingCustomerResponse: true,
                    callback: updateData,
                }),
            },
            completed: {
                title: 'Terminé',
                total: data.completed,
                content: h(TicketTable, {
                    tenant,
                    completed: true,
                    callback: updateData,
                }),
            },
        }"
        :loading="loading"
    />

    <TabLayout
        v-else
        :tabs="{
            recap: {
                title: 'Récapitulatif',
                content: h(TenantReportTicketTable),
            },
            toBeProcessed: {
                title: 'À traiter',
                content: h(TicketTable, {
                    toBeProcessed: true,
                    all: true,
                    callback: updateData,
                }),
                total: data.toBeProcessed,
            },
            inProgress: {
                title: 'En cours',
                content: h(TicketTable, {
                    inProgress: true,
                    all: true,
                    callback: updateData,
                }),
                total: data.inProgress,
            },
            toBeApproved: {
                title: 'À valider',
                content: h(TicketTable, {
                    toBeApproved: true,
                    all: true,
                    callback: updateData,
                }),
                total: data.toBeApproved,
            },
            pendingCustomerResponse: {
                title: 'En attente client',
                content: h(TicketTable, {
                    pendingCustomerResponse: true,
                    all: true,
                    callback: updateData,
                }),
                total: data.pendingCustomerResponse,
            },
            completed: {
                title: 'Terminés',
                content: h(TicketTable, {
                    completed: true,
                    all: true,
                    callback: updateData,
                }),
                total: data.completed,
            },
        }"
        :loading="loading"
    />
</template>
